/**
 * Declarative attribute helpers for tiny, reusable UX patterns.
 *
 * These delegated handlers replace the last few ``on*=`` event
 * attributes that the CSP/no-inline-javascript rule forbids but that
 * don't deserve a full per-page ESM module of their own. Each one is
 * intentionally small and well-isolated; cross-cutting helpers (modal
 * shells, drawers, confirm dialogs) live in their own files.
 *
 * Patterns provided
 * -----------------
 *
 * ``[data-stop-propagation]``
 *   Adds ``event.stopPropagation()`` on ``click`` for the matched
 *   element. The classic use case is an action cell inside a row
 *   that has its own row-level click delegate (open detail page) —
 *   you don't want clicking the action icons to also trigger the
 *   row navigation.
 *
 *   IMPORTANT: bound DIRECTLY on each matched element (not on
 *   ``document.body``) and in BUBBLE phase. Delegated stop-prop on
 *   an ancestor would fire too late — by the time bubbling reaches
 *   ``body``, the row delegate on a closer ancestor has already
 *   handled the event. We therefore re-scan the DOM after every
 *   ``htmx:afterSwap`` and bind once per node via ``_dspBound``.
 *
 *   This does NOT block our own capture-phase delegates (see
 *   ``utils/ui/confirm-action.js`` and ``pages/cleaner-drawer.js``)
 *   because those fire on the way DOWN, before the event reaches the
 *   stopper element on the way back up.
 *
 * ``[data-numeric-only]``
 *   Strips every non-digit character from the input's value on
 *   ``input``. Pairs with ``inputmode="numeric" pattern="[0-9]*"``
 *   for proper mobile keyboards. Implemented as a single delegated
 *   listener on ``document.body`` (bubble phase is fine — there's
 *   no propagation-sensitive contract here).
 *
 * ``[data-print-email]``
 *   Calls ``window.printEmail(triggerElement)`` and prevents the
 *   default link navigation (the trigger is typically an
 *   ``<a href="#">``). ``window.printEmail`` is the public surface
 *   of ``pages/emails.js`` (Phase 21.B): both inbox preview AND
 *   reply page wire their print buttons through this delegate now.
 *   The trigger carries ``data-print-{subject,from,to,date}`` data
 *   attributes; the email body HTML is read from the (only)
 *   ``.email-body-frame`` iframe on the page, with a plain-text
 *   fallback inside ``#email-preview-content`` for inbox messages
 *   that have no HTML body.
 *
 * ``[data-show-cleaner-drawer="<id>"]``
 *   Calls ``window.showCleanerDrawer(parsedId)`` on click. Replaces
 *   the legacy ``onclick="showCleanerDrawer({{ id }})"`` row /
 *   button triggers in the cleaners list, absences list, schedule
 *   sidebar, etc. Phase 21.A migration target.
 *
 *   The trigger lives on a row (``<tr>``) or a clickable cell — pair
 *   with ``[data-stop-propagation]`` on inner action cells so child
 *   icons don't double-trigger this row-level delegate.
 *
 * ``[data-show-visit-drawer="<id>"]``
 *   Calls ``window.showVisitDrawer(parsedId)`` on click. Replaces
 *   ``onclick="window.showVisitDrawer({{ occurrence.id }})"`` on the
 *   stock movement list "Visit #N" link and similar rows.
 *
 *   Both drawer delegates fire on the BUBBLE phase: any ancestor
 *   that legitimately wants to swallow the click can attach its own
 *   ``stopPropagation`` (via ``[data-stop-propagation]`` or directly).
 *   Both are no-ops if the corresponding global helper is missing —
 *   safe defensive-of-context behaviour, mirroring the visit-detail-
 *   drawer's handling of ``window.show*Drawer``.
 *
 * ``[data-soft-reload]``
 *   Calls ``window._softReload()`` on click. Replaces
 *   ``onclick="window._softReload()"`` on the dashboard "Refresh"
 *   button (Phase 21.D) and any other future re-fetch trigger that
 *   wants the same soft-reload semantics (re-issue current URL,
 *   swap ``#main-content`` outerHTML, fall back to hard reload if
 *   HTMX is unavailable). The delegate is intentionally minimal —
 *   no payload, no parameters; the helper itself reads
 *   ``window.location.href``. ``preventDefault`` is applied so this
 *   can be safely used on ``<a>`` triggers as well as ``<button>``.
 *
 * ``[data-dismiss-parent]``
 *   Removes the trigger's parent element from the DOM. Replaces
 *   ``onclick="this.parentElement.remove()"`` on Django messages
 *   close buttons (Phase 22 migration target — used in
 *   ``templates/components/_messages.html`` for the cleaner /
 *   mobile layouts that don't ship the full toast pipeline). Pure
 *   visual dismiss, no server side-effect — Django flashes are
 *   already consumed when the page renders, this just lets the
 *   user hide a stale alert without a full nav. Uses
 *   ``trigger.parentElement`` (matches the original semantics
 *   exactly); pass ``data-dismiss-target="<id>"`` instead if you
 *   need to dismiss a non-parent ancestor.
 *
 * Idempotency
 * -----------
 *
 * - Module-level ``_globalDelegatesBound`` flag for the body-level
 *   delegates (one per SPA lifetime).
 * - Per-element ``_dspBound`` flag for the direct-bind helper.
 *
 * Wired from ``app.js`` on ``DOMContentLoaded`` (initial page) and
 * ``htmx:afterSwap`` / ``htmx:historyRestore`` (re-scan for new
 * ``[data-stop-propagation]`` nodes).
 */

let _globalDelegatesBound = false;

function bindGlobalDelegatesOnce() {
  if (_globalDelegatesBound) return;
  _globalDelegatesBound = true;

  document.body.addEventListener('input', (event) => {
    const target = event.target;
    if (!target || !target.matches || !target.matches('[data-numeric-only]')) return;
    const cleaned = String(target.value || '').replace(/[^0-9]/g, '');
    if (cleaned !== target.value) {
      target.value = cleaned;
    }
  });

  document.body.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-print-email]');
    if (!trigger) return;
    event.preventDefault();
    if (typeof window.printEmail === 'function') {
      window.printEmail(trigger);
    }
  });

  document.body.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-show-cleaner-drawer]');
    if (!trigger) return;
    const raw = trigger.dataset.showCleanerDrawer;
    const id = parseInt(raw, 10);
    if (!Number.isFinite(id)) return;
    if (typeof window.showCleanerDrawer === 'function') {
      window.showCleanerDrawer(id);
    }
  });

  document.body.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-show-visit-drawer]');
    if (!trigger) return;
    const raw = trigger.dataset.showVisitDrawer;
    const id = parseInt(raw, 10);
    if (!Number.isFinite(id)) return;
    if (typeof window.showVisitDrawer === 'function') {
      window.showVisitDrawer(id);
    }
  });

  document.body.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-soft-reload]');
    if (!trigger) return;
    event.preventDefault();
    if (typeof window._softReload === 'function') {
      window._softReload();
    } else {
      window.location.reload();
    }
  });

  document.body.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-dismiss-parent]');
    if (!trigger) return;
    event.preventDefault();
    const parent = trigger.parentElement;
    if (parent) parent.remove();
  });
}

/**
 * Re-scan the DOM and direct-bind a click stopPropagation handler on
 * every element flagged with ``data-stop-propagation``. Idempotent
 * per-node (``_dspBound`` flag).
 */
function scanStopPropagationTargets(root) {
  const scope = root && root.querySelectorAll ? root : document;
  const nodes = scope.querySelectorAll('[data-stop-propagation]');
  nodes.forEach((el) => {
    if (el._dspBound) return;
    el._dspBound = true;
    el.addEventListener('click', (event) => {
      event.stopPropagation();
    });
  });
}

/**
 * Public init. Safe to call repeatedly — global listeners are
 * guarded by ``_globalDelegatesBound`` and per-node ``_dspBound``.
 *
 * @param {Element|Document} [root=document] - Scan scope for the
 *   ``data-stop-propagation`` direct-bind step. ``app.js`` passes
 *   ``event.detail.target`` after ``htmx:afterSwap`` so we only
 *   re-scan the freshly-injected subtree.
 */
export function initDeclarativeAttrs(root) {
  bindGlobalDelegatesOnce();
  scanStopPropagationTargets(root);
}
